import React, { useState, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Switch,
    FormControlLabel,
    Divider,
    Alert,
    Snackbar,
    CircularProgress,
    Grid,
} from '@mui/material';
import { Assessment as AssessmentIcon } from '@mui/icons-material';
import useFetch from '../../../../hooks/useFetch';
import api from '../../../../config/axiosConfig';
import RTL from '../../../../utils/RTL';

const API_ENDPOINT = '/institutes/api/v1/teacher-working-days/daily-report-contents/';

function DailyReportSettings() {
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [isSaving, setIsSaving] = useState(false);

    const { data, loading, error } = useFetch(API_ENDPOINT);

    const [available, setAvailable] = useState([]);
    const [selected, setSelected] = useState([]);

    React.useEffect(() => {
        if (data) {
            setAvailable(data.available || []);
            setSelected(data.selected || []);
        }
    }, [data]);

    const handleToggle = useCallback(async (value) => {
        const isOn = selected.includes(value);
        const nextSelected = isOn
            ? selected.filter((v) => v !== value)
            : [...selected, value];

        const previousSelected = selected;
        // Optimistic update
        setSelected(nextSelected);
        setIsSaving(true);

        try {
            await api.patch(API_ENDPOINT, { selected: nextSelected });
            setSnackbar({
                open: true,
                message: 'تنظیمات با موفقیت به‌روزرسانی شد.',
                severity: 'success',
            });
        } catch (err) {
            // Revert on error
            setSelected(previousSelected);
            const errorMessage = err?.response?.data?.detail
                || err?.response?.data?.error
                || err?.response?.data?.selected?.[0]
                || 'خطا در به‌روزرسانی تنظیمات';
            setSnackbar({
                open: true,
                message: errorMessage,
                severity: 'error',
            });
        } finally {
            setIsSaving(false);
        }
    }, [selected]);

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box p={3}>
                <Alert severity="error">
                    {error || 'خطا در بارگذاری تنظیمات گزارش روزانه'}
                </Alert>
            </Box>
        );
    }

    return (
        <RTL>
            <Box sx={{ p: 3 }}>
                <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
                    تنظیمات گزارش روزانه
                </Typography>

                <Paper elevation={2} sx={{ p: 3 }}>
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <AssessmentIcon color="primary" />
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            محتویات گزارش روزانه
                        </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        موارد انتخاب‌شده در گزارش روزانه‌ای که برای مدیریت ارسال می‌شود گنجانده می‌شوند.
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    {available.length === 0 ? (
                        <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
                            موردی برای انتخاب وجود ندارد.
                        </Alert>
                    ) : (
                        <Grid container spacing={1} rowSpacing={2}>
                            {available.map((item) => (
                                <Grid item xs={12} sm={6} key={item.value}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={selected.includes(item.value)}
                                                onChange={() => handleToggle(item.value)}
                                                disabled={isSaving}
                                            />
                                        }
                                        label={item.label}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </Paper>

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert
                        onClose={handleCloseSnackbar}
                        severity={snackbar.severity}
                        sx={{ width: '100%' }}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Box>
        </RTL>
    );
}

export default DailyReportSettings;
